<template>
  <main id="page-poll-show" class="news-page">

    <!-- ── Masthead minimal ───────────────────── -->
    <header class="masthead masthead--compact">
      <div class="masthead__rule masthead__rule--thick"></div>
      <div class="masthead__inner masthead__inner--row">
        <button type="button" class="poll-show__back-btn" @click="$router.back()">
          <i class="mdi mdi-arrow-left" aria-hidden="true"></i> Înapoi
        </button>
        <div class="poll-show__header-actions">
          <button type="button" class="poll-show__download-btn" :disabled="!poll" @click="downloadOgImage">
            <i class="mdi mdi-download" aria-hidden="true"></i>
          </button>
          <button type="button" class="poll-show__share-btn" :disabled="!poll" @click="sharePoll">
            <i class="mdi mdi-share-variant" aria-hidden="true"></i> Distribuie
          </button>
          <button type="button" class="poll-show__share-btn poll-show__share-btn--facebook" :disabled="!poll" @click="shareOnFacebook">
            <i class="mdi mdi-facebook" aria-hidden="true"></i> Facebook
          </button>
        </div>
      </div>
      <div class="masthead__rule masthead__rule--thin"></div>
    </header>

    <!-- ── Poll Content ───────────────────────── -->
    <template v-if="poll">
      <section class="poll-show__body" :class="{ 'poll-show__body--has-results': showResults }">
        <div class="poll-show__title-card" :class="{ 'poll-show__title-card--reported': isPollReportedByMe }">
          <h1 class="poll-show__question">{{ poll.title }}</h1>

          <!-- 3-dots report menu — positioned outside padding, top-right corner -->
          <div v-if="canReportPoll || canEditPoll" class="poll-show__title-menu" ref="titleMenuEl">
            <button type="button" class="poll-show__title-menu-btn" :aria-expanded="showTitleMenu" @click.stop="showTitleMenu = !showTitleMenu" title="Opțiuni">
              <i class="mdi mdi-dots-vertical" aria-hidden="true"></i>
            </button>
            <div v-if="showTitleMenu" class="poll-show__title-menu-dropdown">
              <button v-if="canEditPoll" type="button" class="poll-show__title-menu-item poll-show__title-menu-item--edit" @click="goToEdit">
                <i class="mdi mdi-pencil-outline" aria-hidden="true"></i>
                Editează
              </button>
              <button v-if="canReportPoll" type="button" class="poll-show__title-menu-item" :class="{ 'poll-show__title-menu-item--reported': isPollReportedByMe }" :disabled="isPollReporting || isPollReportedByMe" @click="reportPoll">
                <i class="mdi mdi-flag-outline" aria-hidden="true"></i>
                {{ isPollReportedByMe ? 'Raportat' : (isPollReporting ? 'Se raportează...' : 'Raportează') }}
              </button>
            </div>
          </div>
        </div>

        <!-- "Ai raportat acest conținut" label — outside card, below border -->
        <div v-if="isPollReportedByMe" class="poll-show__reported-label">
          <i class="mdi mdi-flag" aria-hidden="true"></i> Ai raportat acest conținut
        </div>

        <div class="poll-show__below-card">

          <!-- Rejection banner (visible to all users when poll is rejected) -->
          <div v-if="poll.status === 'rejected'" class="poll-show__rejected-banner">
            <strong>Sondaj respins</strong>
            <p class="poll-show__rejected-reason">{{ poll.rejection_reason }}</p>
          </div>

          <!-- Poll body hidden for non-authors when poll is rejected -->
          <div v-if="poll.status === 'rejected' && !isPollOwner" class="poll-show__rejected-hidden">
            Conținutul acestui sondaj nu este disponibil.
          </div>

          <template v-if="isPollContentVisible">

            <!-- Views count + reactions bar -->
            <div class="poll-show__meta-bar">
              <span class="poll-show__views-count">
            <i class="mdi mdi-eye" aria-hidden="true"></i> {{ poll.views_count }} {{ poll.views_count === 1 ? 'vizualizare' : 'vizualizări' }}
          </span>
              <div class="poll-show__reactions">
                <button type="button" class="poll-show__reaction-btn" :class="{ 'poll-show__reaction-btn--active': poll.user_reaction === 'upvote' }" :disabled="isReacting" @click="toggleReaction('upvote')">
                  <i class="mdi mdi-thumb-up" aria-hidden="true"></i> {{ poll.likes_count }}
                </button>
                <button type="button" class="poll-show__reaction-btn poll-show__reaction-btn--dislike" :class="{
                  'poll-show__reaction-btn--active': poll.user_reaction === 'downvote',
                  'poll-show__reaction-btn--active-dislike': poll.user_reaction === 'downvote'
                }" :disabled="isReacting" @click="toggleReaction('downvote')">
                  <i class="mdi mdi-thumb-down" aria-hidden="true"></i> {{ poll.dislikes_count }}
                </button>
              </div>
            </div>

            <!-- Already voted notice -->
            <div v-if="alreadyVoted && !voteSuccess" class="poll-show__voted-notice">
              <i class="mdi mdi-check" aria-hidden="true"></i> Ați votat deja la acest sondaj.<template v-if="revokeAvailable"> &mdash; <button type="button" class="poll-show__revoke-btn" :disabled="isRevoking" @click="revokeVote">{{ isRevoking ? 'Se revocă...' : 'Revocă vot' }}</button></template>
            </div>

            <!-- Success notice -->
            <div v-if="voteSuccess" class="poll-show__success-notice">
              <i class="mdi mdi-check" aria-hidden="true"></i> Răspunsul a fost înregistrat cu succes!<template v-if="revokeAvailable"> &mdash; <button type="button" class="poll-show__revoke-btn" :disabled="isRevoking" @click="revokeVote">{{ isRevoking ? 'Se revocă...' : 'Revocă vot' }}</button></template>
            </div>

            <!-- Vote error notice -->
            <div v-if="errorMessage" class="poll-show__error-notice">
              {{ errorMessage }}
            </div>

            <!-- Share success notice -->
            <div v-if="shareMessage" class="poll-show__share-notice">
              <i class="mdi mdi-check" aria-hidden="true"></i> {{ shareMessage }}
            </div>

            <!-- Options -->
            <ul class="poll-show__options" role="listbox" aria-label="Variante de răspuns">
              <li v-for="option in poll.options" :key="option.id" class="poll-option" :class="pollOptionClasses(option)" role="option" :aria-selected="selectedOptionId === option.id" tabindex="0" @click="selectOption(option.id)" @keydown.enter="selectOption(option.id)" @keydown.space.prevent="selectOption(option.id)">
                <span v-if="showResults" class="poll-option__fill" :class="{ 'poll-option__fill--chosen': poll.user_vote?.option_id === option.id }" :style="{ width: animateResults ? (poll.vote_results?.[option.id]?.percentage ?? 0) + '%' : '0%' }"></span>
                <span v-if="showResults && poll.user_vote?.option_id === option.id" class="poll-option__stamp" aria-hidden="true"><i class="mdi mdi-check" aria-hidden="true"></i> Votat</span>
                <span v-if="!showResults" class="poll-option__marker"></span>
                <span class="poll-option__label">{{ option.title }}</span>
                <span v-if="showResults" class="poll-option__stats">
                  {{ voteStats(option) }}
                </span>
              </li>
            </ul>

            <!-- ── Sondaje corelate ─────────────────────── -->
            <section v-if="poll.similar_polls && poll.similar_polls.length > 0" class="poll-show__similar">
              <h2 class="poll-show__similar-title">Sondaje corelate</h2>
              <ul class="poll-show__similar-list">
                <li v-for="similar in poll.similar_polls" :key="similar.id" class="poll-show__similar-item" :class="{ 'poll-show__similar-item--voted': similar.user_has_voted }" @click="$router.push({ name: 'polls.show', params: { id: similar.id, slug: similar.slug } })">
                <span class="poll-show__similar-label">{{ similar.title }}</span>
                <span v-if="similar.user_has_voted" class="poll-show__similar-voted-stamp"><i class="mdi mdi-check" aria-hidden="true"></i> Votat</span>
                <span class="poll-show__similar-arrow">→</span>
              </li>
              </ul>
            </section>

            <!-- ── Argumentări (comentarii) ────────────── -->
            <PollComments v-if="poll" ref="pollComments" :poll-id="poll.id" :has-voted="alreadyVoted || voteSuccess" :voted-option-id="poll.user_vote?.option_id" :current-user-id="currentUser ? currentUser.id : null" @need-auth="showAuthModal = true" @reported="onCommentReportedFromChild" />

          </template><!-- /isPollContentVisible -->

        </div><!-- /poll-show__below-card -->
      </section>
    </template>

    <!-- ── Toast post-raportare ────────────────── -->
    <transition name="comment-toast">
      <div v-if="showReportToast" class="poll-show__comment-toast poll-show__comment-toast--report">
        <span class="poll-show__comment-toast-text">
      <i class="mdi mdi-flag-checkered" aria-hidden="true"></i> Raportarea ta a fost înregistrată. Luăm în serios fiecare sesizare și îți mulțumim pentru că ajuți la menținerea calității conținutului.
    </span>
        <button type="button" class="poll-show__comment-toast-close" @click="showReportToast = false"><i class="mdi mdi-close" aria-hidden="true"></i></button>
      </div>
    </transition>

    <!-- ── Toast post-vot ──────────────────────── -->
    <transition name="comment-toast">
      <div v-if="showCommentToast" class="poll-show__comment-toast">
        <span class="poll-show__comment-toast-text">
      <i class="mdi mdi-comment-text" aria-hidden="true"></i> Dorești să îți argumentezi alegerea?
      <a href="#poll-comments" class="poll-show__comment-toast-link" @click.prevent="scrollToComments">
        Scrie o argumentare ↓
      </a>
    </span>
        <button type="button" class="poll-show__comment-toast-close" @click="showCommentToast = false"><i class="mdi mdi-close" aria-hidden="true"></i></button>
      </div>
    </transition>

    <!-- ── Sticky Vote Bar ────────────────────── -->
    <StickyFooter :show="showVoteBar">
      <div class="poll-show__vote-bar-inner">
        <button type="button" class="poll-show__vote-btn" :disabled="isSubmitting" @click="submitVote">
          {{ isSubmitting ? "Se procesează..." : "Înregistrează răspuns" }}
        </button>
      </div>
    </StickyFooter>

    <!-- ── Auth Modal (pentru utilizatori neautentificați) ── -->
    <AuthModal v-if="showAuthModal" @close="showAuthModal = false" @success="onAuthSuccess" />

  </main>
</template>

<script>
import showable from '@app/mixins/showable.js';
import authMixin from '@app/mixins/authMixin.js';
import recaptchaMixin from '@app/mixins/recaptchaMixin.js';
import AuthModal from '@app/components/AuthModal.vue';
import PollComments from './partials/PollComments.vue';
import StickyFooter from '@app/components/StickyFooter.vue';

export default {
  name: "PollShowView",

  components: { AuthModal, PollComments, StickyFooter },

  mixins: [showable, authMixin, recaptchaMixin],

  data() {
    return {
      fetchModel: true,
      poll: null,
      selectedOptionId: null,
      isSubmitting: false,
      isRevoking: false,
      voteSuccess: false,
      errorMessage: null,
      shareMessage: null,
      animateResults: false,
      revokeAvailable: false,
      showAuthModal: false,
      pendingOptionId: null,
      isReacting: false,
      isPollReporting: false,
      showTitleMenu: false,
      showReportToast: false,
      showCommentToast: false,
    };
  },

  computed: {
    alreadyVoted() {
      return this.poll?.user_vote != null;
    },
    showResults() {
      return this.alreadyVoted || this.voteSuccess;
    },
    showVoteBar() {
      return this.selectedOptionId !== null && !this.alreadyVoted && !this.voteSuccess;
    },
    isPollOwner() {
      return !!(this.currentUser && this.poll && this.currentUser.id === this.poll.user_id);
    },
    isPollContentVisible() {
      return !this.poll || this.poll.status !== 'rejected' || this.isPollOwner;
    },
    canReportPoll() {
      return !!(this.currentUser && this.poll && !this.isPollOwner);
    },
    canEditPoll() {
      return !!(this.currentUser && this.poll && this.isPollOwner && this.poll.is_editable);
    },
    isPollReportedByMe() {
      return !!(this.poll?.my_report);
    },
  },

  watch: {
    showResults(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => { this.animateResults = true; }, 20);
        });
      } else {
        this.animateResults = false;
      }
    },
    'poll.user_vote'(val) {
      const createdAt = val?.created_at;
      this.revokeAvailable = !!(createdAt && (Date.now() - new Date(createdAt).getTime() < 24 * 60 * 60 * 1000));
    },
    poll(newVal) {
      if (newVal && this.$route.query.focus_comment === '1') {
        setTimeout(() => {
          this.scrollToComments();
        }, 300);
      }
    },
  },

  mounted() {
    this.fetchCurrentUser();
    document.addEventListener('click', this._onDocClickTitle);
  },

  beforeUnmount() {
    document.removeEventListener('click', this._onDocClickTitle);
  },

  methods: {

    pollOptionClasses(option) {
      return {
        'poll-option--selected':  this.selectedOptionId === option.id,
        'poll-option--voted':     this.poll.user_vote?.option_id === option.id,
        'poll-option--disabled':  this.alreadyVoted || this.voteSuccess,
      };
    },

    selectOption(optionId) {
      if (this.alreadyVoted || this.voteSuccess) return;
      this.selectedOptionId = optionId;
    },

    _onDocClickTitle(e) {
      if (this.showTitleMenu && this.$refs.titleMenuEl && !this.$refs.titleMenuEl.contains(e.target)) {
        this.showTitleMenu = false;
      }
    },

    goToEdit() {
      this.showTitleMenu = false;
      this.$router.push({ name: 'polls.edit', params: { id: this.poll.id } });
    },

    async reportPoll() {
      if (this.isPollReporting || this.isPollReportedByMe) return;
      this.showTitleMenu = false;
      this.isPollReporting = true;
      try {
        await window.axios.post('content-reports', {
          content_id: this.poll.id,
          content_type: 'poll',
        });
        this.poll.my_report = { content_type: 'poll', content_id: this.poll.id };
        this.triggerReportToast();
      } catch (err) {
        console.error('[reportPoll] Failed to report poll:', err?.response?.data || err);
      } finally {
        this.isPollReporting = false;
      }
    },

    onCommentReportedFromChild() {
      this.triggerReportToast();
    },

    triggerReportToast() {
      this.showReportToast = true;
      setTimeout(() => { this.showReportToast = false; }, 6000);
    },

    async submitVote() {
      if (!this.selectedOptionId || this.isSubmitting) return;

      if (!this.currentUser) {
        this.pendingOptionId = this.selectedOptionId;
        this.showAuthModal = true;
        return;
      }

      await this.doVote(this.selectedOptionId);
    },

    async onAuthSuccess({ user, token }) {
      this.currentUser = user;
      if (token) {
        window.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      }
      this.showAuthModal = false;

      if (this.pendingOptionId) {
        const optionId = this.pendingOptionId;
        this.pendingOptionId = null;
        this.selectedOptionId = optionId;
        await this.doVote(optionId);
      }
    },

    async doVote(optionId) {
      this.isSubmitting = true;
      this.errorMessage = null;
      try {
        const recaptchaToken = await this.getRecaptchaToken('vote');
        await window.axios.post(`polls/${this.$route.params.id}/responses`, {
          option_id: optionId,
          recaptcha_token: recaptchaToken,
        });
        this.voteSuccess = true;
        this.showCommentToast = true;
        await this.fetchData();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message || "A apărut o eroare. Încearcă din nou.";
      } finally {
        this.isSubmitting = false;
      }
    },

    async revokeVote() {
      if (this.isRevoking) return;
      const confirmed = window.confirm(
        'Ești sigur că vrei să revoci votul?\n\n' +
        'Orice comentariu lăsat în susținerea acestuia, împreună cu toate răspunsurile primite, va fi șters definitiv.'
      );
      if (!confirmed) return;
      this.isRevoking = true;
      this.errorMessage = null;
      try {
        await window.axios.delete(`polls/${this.$route.params.id}/responses/revoke`);
        this.voteSuccess = false;
        this.selectedOptionId = null;
        this.showCommentToast = false;
        await this.fetchData();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message || "A apărut o eroare la revocare. Încearcă din nou.";
      } finally {
        this.isRevoking = false;
      }
    },

    voteStats(option) {
      const result = this.poll.vote_results?.[option.id];
      if (!result) return '';
      const { percentage, votes_count } = result;
      return `${percentage}% · ${votes_count} ${votes_count === 1 ? 'vot' : 'voturi'}`;
    },

    async toggleReaction(value) {
      if (!this.currentUser) {
        this.showAuthModal = true;
        return;
      }
      if (this.isReacting) return;
      this.isReacting = true;
      try {
        if (this.poll.user_reaction === value) {
          const resp = await window.axios.delete(`polls/${this.$route.params.id}/assessments/revoke`);
          this.poll.user_reaction = resp.data.user_reaction;
          this.poll.likes_count   = resp.data.likes_count;
          this.poll.dislikes_count = resp.data.dislikes_count;
        } else {
          const resp = await window.axios.post(`polls/${this.$route.params.id}/assessments`, { value });
          this.poll.user_reaction = resp.data.user_reaction;
          this.poll.likes_count   = resp.data.likes_count;
          this.poll.dislikes_count = resp.data.dislikes_count;
        }
      } catch {
        // silent fail — counts remain unchanged
      } finally {
        this.isReacting = false;
      }
    },

    trackShare() {
      if (this.currentUser) {
        window.axios.post(`polls/${this.$route.params.id}/share`).catch(() => { });
      }
    },

    shareOnFacebook() {
      const url = new URL(this.poll.url);
      url.searchParams.set('utm_source', 'facebook');
      url.searchParams.set('utm_medium', 'social');
      url.searchParams.set('utm_campaign', 'poll_share');

      const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url.toString())}`;

      this.trackShare();

      const popup = window.open(shareUrl, 'facebook-share', 'width=626,height=436');

      if (popup) {
        popup.opener = null;
      }
    },

    async sharePoll() {
      const url = this.poll.url;
      const title = this.poll?.title || "Sondaj de opinie";

      this.trackShare();

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
        } catch {
          // user cancelled share — no action needed
        }
      } else {
        try {
          await navigator.clipboard.writeText(url);
          this.shareMessage = "Linkul a fost copiat în clipboard!";
          setTimeout(() => { this.shareMessage = null; }, 3000);
        } catch {
          prompt("Copiați linkul:", url);
        }
      }
    },

    async downloadOgImage() {
      const id = this.$route.params.id;
      const url = this.poll.url;

      try {
        const resp = await window.axios.get(`polls/${id}/download-image`, {
          responseType: 'blob',
        });

        const objectUrl = URL.createObjectURL(resp.data);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `sondaj-${id}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);

        try {
          await navigator.clipboard.writeText(url);
        } catch {
          // clipboard unavailable — silently ignore
        }

        this.shareMessage = "Link sondaj copiat în clipboard!";
        setTimeout(() => { this.shareMessage = null; }, 3000);
      } catch {
        // download failed — silently ignore
      }
    },

    scrollToComments() {
      this.showCommentToast = false;
      this.$nextTick(() => {
        const el = document.getElementById('poll-comments');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => {
          if (this.$refs.pollComments) {
            this.$refs.pollComments.focusEditor();
          }
        }, 400);
      });
    },
  },
};
</script>
